import { FetchEventSource, type ApiEventSource } from './stream'
export type { ApiEventSource } from './stream'
/** Framework-independent transport. Configuration contains public endpoints only. */
export interface ClientConfig {
  apiBaseUrl?: string
  authBaseUrl?: string
  fetch?: typeof globalThis.fetch
  getAccessToken?: () => string | null | Promise<string | null>
  onAuthToken?: (token: string | null) => void | Promise<void>
  onSessionExpired?: () => void | Promise<void>
}
let config: ClientConfig = {}
let sessionGeneration = 0
const sessionClosers = new Set<() => void>()

/** Call after logout/account changes, before discarding user-scoped caches. */
export function resetClientSession() {
  sessionGeneration += 1
  for (const close of [...sessionClosers]) close()
}
function assertSession(generation: number) {
  if (generation !== sessionGeneration) throw new DOMException('Session changed', 'AbortError')
}
export function captureClientSession() {
  const generation = sessionGeneration
  return {
    isCurrent: () => generation === sessionGeneration,
    assertCurrent: () => assertSession(generation),
    onReset(callback: () => void) {
      const close = () => { sessionClosers.delete(close); callback() }
      if (generation !== sessionGeneration) callback()
      else sessionClosers.add(close)
      return () => { sessionClosers.delete(close) }
    },
  }
}

function normalizeBase(value: string | undefined): string {
  if (!value) return ''
  const url = new URL(value)
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error('Client endpoint must be an HTTP(S) URL without credentials, query, or fragment')
  }
  return url.href.replace(/\/$/, '')
}

export function configureClient(next: ClientConfig = {}) {
  const validated = { ...next, apiBaseUrl: normalizeBase(next.apiBaseUrl), authBaseUrl: normalizeBase(next.authBaseUrl) }
  resetClientSession()
  config = validated
}

export function usesBearerAuthentication() { return Boolean(config.getAccessToken) }

export function authBaseUrl() { return config.authBaseUrl || config.apiBaseUrl || undefined }

/** better-auth transport: native credentials stay in the supplied OS storage adapter. */
export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!config.getAccessToken) return (config.fetch || globalThis.fetch)(input, init)
  const generation = sessionGeneration
  const base = new URL(authBaseUrl() || '')
  const request = new Request(input, init)
  const url = new URL(request.url)
  const authPath = base.pathname.replace(/\/$/, '') || '/api/auth'
  if (url.origin !== base.origin || !url.pathname.startsWith(`${authPath}/`) || url.username || url.password) throw new Error('Auth request escapes the configured endpoint')
  const token = await config.getAccessToken()
  assertSession(generation)
  const headers = new Headers(request.headers)
  if (token) headers.set('authorization', `Bearer ${token}`)
  const response = await (config.fetch || globalThis.fetch)(new Request(request, { headers, credentials: 'omit', redirect: 'error' }))
  if (generation !== sessionGeneration) { await response.body?.cancel().catch(() => {}); assertSession(generation) }
  if (response.ok) {
    const next = response.headers.get('set-auth-token')
    if (next || url.pathname === `${authPath}/sign-out`) {
      if (!config.onAuthToken) throw new Error('Native credential storage is not configured')
      await config.onAuthToken(url.pathname === `${authPath}/sign-out` ? null : next)
    }
  }
  return response
}

/** Restrict authenticated requests to this API, including already-resolved URLs. */
export function apiUrl(path: string): string {
  const base = config.apiBaseUrl || ''
  if (base && path.startsWith(`${base}/api/`)) path = path.slice(base.length)
  if (!path.startsWith('/api/') || path.includes('\\')) throw new Error('Expected an application API path')
  const normalized = new URL(path, 'https://api.invalid')
  if (normalized.origin !== 'https://api.invalid' || !normalized.pathname.startsWith('/api/')) {
    throw new Error('API path escapes the configured endpoint')
  }
  return `${base}${normalized.pathname}${normalized.search}`
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
  }
}

/** No implicit retries: callers must explicitly decide whether a mutation is safe to repeat. */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const generation = sessionGeneration
  const url = apiUrl(path)
  const headers = new Headers(init.headers)
  const token = config.getAccessToken ? await config.getAccessToken() : null
  assertSession(generation)
  if (token) headers.set('authorization', `Bearer ${token}`)
  const response = await (config.fetch || globalThis.fetch)(url, {
    ...init, headers,
    credentials: config.getAccessToken ? 'omit' : config.apiBaseUrl ? 'include' : 'same-origin',
    // Do not forward credentials through an unexpected cross-origin API redirect.
    redirect: 'error',
  })
  if (generation !== sessionGeneration) { await response.body?.cancel().catch(() => {}); assertSession(generation) }
  if (response.status === 401 && token && config.onSessionExpired) await config.onSessionExpired()
  return response
}

async function assertResponse(response: Response) {
  if (!response.ok) {
    let message = response.statusText
    let code: string | undefined
    let details: Record<string, unknown> | undefined
    try {
      const data = await response.json()
      message = data?.error?.message ?? message
      code = data?.error?.code
      details = data?.error
    } catch { /* Preserve the HTTP error when a proxy returns non-JSON. */ }
    throw new ApiError(response.status, message, code, details)
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const generation = sessionGeneration
  const { json: body, ...rest } = init
  const headers = new Headers(rest.headers)
  if (body !== undefined) headers.set('content-type', 'application/json')
  const response = await apiFetch(path, {
    ...rest, headers, body: body !== undefined ? JSON.stringify(body) : rest.body,
  })
  await assertResponse(response)
  if (response.status === 204) return undefined as T
  const result = await response.json() as T
  assertSession(generation)
  return result
}

export async function apiBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const generation = sessionGeneration
  const controller = new AbortController()
  const abort = () => controller.abort(init.signal?.reason)
  const close = () => controller.abort(new DOMException('Session changed', 'AbortError'))
  sessionClosers.add(close)
  init.signal?.addEventListener('abort', abort, { once: true })
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined
  const cancelBody = () => { void reader?.cancel(controller.signal.reason).catch(() => {}) }
  controller.signal.addEventListener('abort', cancelBody, { once: true })
  try {
    if (init.signal?.aborted) abort()
    controller.signal.throwIfAborted()
    const response = await apiFetch(path, { ...init, signal: controller.signal })
    await assertResponse(response)
    reader = response.body?.getReader()
    // Own the reader so cancellation also stops a body returned by an adapter
    // that does not propagate fetch's AbortSignal into its response stream.
    const chunks: BlobPart[] = []
    while (reader) {
      controller.signal.throwIfAborted()
      const { done, value } = await reader.read()
      controller.signal.throwIfAborted()
      if (done) break
      chunks.push(new Uint8Array(value))
    }
    controller.signal.throwIfAborted()
    assertSession(generation)
    return new Blob(chunks, { type: response.headers.get('content-type') ?? '' })
  } finally {
    sessionClosers.delete(close)
    init.signal?.removeEventListener('abort', abort)
    controller.signal.removeEventListener('abort', cancelBody)
    await reader?.cancel().catch(() => {})
    reader?.releaseLock()
  }
}

export function createApiEventSource(path: string): ApiEventSource {
  apiUrl(path) // Validate before opening either transport.
  const stream = config.getAccessToken || typeof EventSource === 'undefined'
    ? new FetchEventSource(path, apiFetch)
    : new EventSource(apiUrl(path), { withCredentials: true })
  const close = stream.close.bind(stream)
  const onError = () => { if (stream.readyState === 2) stream.close() }
  stream.close = () => {
    sessionClosers.delete(stream.close)
    stream.removeEventListener('error', onError)
    close()
  }
  sessionClosers.add(stream.close)
  stream.addEventListener('error', onError)
  return stream
}

export const wsApi = (slug: string) => `/api/w/${encodeURIComponent(slug)}`
